import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import { Label } from '@strata-monorepo/shared-ui/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@strata-monorepo/shared-ui/components/ui/radio-group'

interface RadioButtonGroupProps {
  options: { label: string; value: string; subtext?: string }[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  className?: string
  ariaLabel?: string
  gridClassName?: string
}

export function RadioButtonGroup({
  options,
  value,
  defaultValue,
  onChange,
  className,
  ariaLabel,
  gridClassName,
}: RadioButtonGroupProps): React.JSX.Element {
  const selected = value || defaultValue

  const selectedStyles = 'strata:border-[#415973] strata:text-gray-900 strata:bg-[#EFF4FB]'
  const unselectedStyles =
    'strata:border-[#CECED1] strata:bg-white strata:text-gray-700 strata:hover:border-[#415973]'

  const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>, value: string): void => {
    if (e.key === 'Enter') {
      onChange?.(value)
    }
  }

  return (
    <div
      aria-label={ariaLabel}
      className={cn('strata:mx-auto strata:w-full', className)}
    >
      <RadioGroup
        value={selected}
        onValueChange={onChange}
        className={cn('strata:grid strata:gap-4', gridClassName)}
      >
        {options.map((option) => {
          return (
            <div
              key={option.value}
              className='strata:flex strata:items-center strata:space-x-0'
            >
              <RadioGroupItem
                value={option.value}
                id={option.value}
                className='strata:sr-only'
                tabIndex={-1}
              />
              <Label
                onKeyDown={(e) => handleKeyDown(e, option.value)}
                aria-label={option.label}
                tabIndex={0}
                htmlFor={option.value}
                data-testid={`radio-button-${option.value}`}
                className={`strata:flex-1 strata:cursor-pointer strata:rounded-sm strata:border strata:p-3 strata:text-center strata:transition-all strata:duration-200 strata:font-bold strata:flex strata:flex-col strata:items-center strata:justify-center ${
                  selected === option.value ? selectedStyles : unselectedStyles
                } `}
              >
                <span className='strata:font-bold strata:text-sm strata:md:text-base'>
                  {option.label}
                </span>
                {option.subtext && (
                  <span
                    className={`strata:text-xs strata:md:text-sm strata:mt-0.5 strata:md:mt-1 strata:font-normal strata:leading-tight ${
                      selected === option.value ? 'strata:text-gray-700' : 'strata:text-gray-600'
                    }`}
                  >
                    {option.subtext}
                  </span>
                )}
              </Label>
            </div>
          )
        })}
      </RadioGroup>
    </div>
  )
}
